import React, { useState } from "react";
import { formatCurrency } from "../../../../utils/format";
import { formatDate } from "../../../../utils/date";
import { PAYABLE_STATUS, PAYABLE_STATUS_OPTIONS } from "../constants";
import { ConfirmDialog, NeoBadge, NeoButton, NeoFinancialTable, NeoInput } from "../../../../components/Common";
import PaymentConfirmModal from "../../components/PaymentConfirmModal";
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from "../../../../constants/financial";

/**
 * PayableTable
 * Tabela de contas a pagar usando NeoFinancialTable.
 * Inclui summary inline, filtro por status, busca, expansão e ações.
 */
const PayableTable = ({
    records, summary, processing,
    search, statusFilter,
    onSearchChange, onStatusChange,
    onMarkAsPaid, onCancel, onLoadMore, hasMore, loading,
}) => {

    // Estado do modal de baixa
    const [paymentRecord, setPaymentRecord] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const handleMarkPaid = (record) => {
        setPaymentRecord(record);
        setShowPaymentModal(true);
    };

    const handleConfirmPayment = async (recordId, paymentData) => {
        const success = await onMarkAsPaid(recordId, paymentData);
        if (success) {
            setShowPaymentModal(false);
            setPaymentRecord(null);
        }
    };

    const handleCancel = async (record) => {
        const confirmed = await ConfirmDialog({
            title: "Cancelar Despesa",
            text: `Deseja cancelar esta despesa de ${formatCurrency(record.grossAmount)}?\nEsta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, cancelar",
            cancelButtonText: "Voltar",
        });
        if (confirmed) onCancel(record.id);
    };

    const getStatusBadge = (status) => {
        const s = PAYABLE_STATUS[status] || PAYABLE_STATUS.PENDING;
        const badgeColor = s.color === "secondary" ? "secondary" : s.color;
        return (
            <NeoBadge color={badgeColor} solid={false}>
                {s.label}
            </NeoBadge>
        );
    };

    // ── Colunas para o NeoFinancialTable ──
    const payableColumns = [
        {
            key: 'description',
            label: 'Descrição',
            render: (row, rowIndex, { page, rowsPerPage }) => {
                const indexNum = String((page - 1) * rowsPerPage + rowIndex + 1).padStart(2, '0');
                return (
                    <div>
                        <span className="fw-bold d-block text-brand-primary font-size-12">
                            <span className="text-brand-secondary me-2">{indexNum}.</span>
                            {row.description || "—"}
                        </span>
                        {row.observation && (
                            <small className="d-block text-muted font-size-11 neo-font-outfit mt-0.5">
                                {row.observation}
                            </small>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'categoryName',
            label: 'Categoria',
            width: '140px',
            render: (row) => (
                <span className="font-size-12 fw-semibold text-muted">
                    {row.categoryName || "—"}
                </span>
            )
        },
        {
            key: 'dueDate',
            label: 'Vencimento',
            width: '120px',
            render: (row) => (
                <span className="fw-semibold font-size-12 text-dark">
                    {formatDate(row.dueDate)}
                </span>
            )
        },
        {
            key: 'paymentDate',
            label: 'Pagamento',
            width: '120px',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    {formatDate(row.paymentDate)}
                </span>
            )
        },
        {
            key: 'entityName',
            label: 'Fornecedor',
            width: '150px',
            render: (row) => (
                <span className="font-size-12 fw-semibold text-muted">
                    {row.entityName || "—"}
                </span>
            )
        },
        {
            key: 'grossAmount',
            label: 'Valor',
            width: '130px',
            align: 'right',
            render: (row) => (
                <span className="fw-bold font-size-13 text-danger">
                    {formatCurrency(row.grossAmount)}
                </span>
            )
        },
        {
            key: 'status',
            label: 'Status',
            width: '120px',
            align: 'center',
            render: (row) => getStatusBadge(row.status)
        },
        {
            key: 'actions',
            label: 'Ações',
            width: '100px',
            align: 'center',
            render: (row) => {
                if (row.status !== FINANCIAL_STATUS.PENDING && row.status !== FINANCIAL_STATUS.OVERDUE) return null;
                return (
                    <div className="d-flex gap-1 justify-content-center">
                        <NeoButton
                            size="sm"
                            variant="outline-success"
                            square
                            disabled={processing}
                            onClick={(e) => { e.stopPropagation(); handleMarkPaid(row); }}
                            title="Dar baixa"
                            icon="mdi-check"
                        />
                        <NeoButton
                            size="sm"
                            variant="outline-danger"
                            square
                            disabled={processing}
                            onClick={(e) => { e.stopPropagation(); handleCancel(row); }}
                            title="Cancelar"
                            icon="mdi-close"
                        />
                    </div>
                );
            }
        }
    ];

    // ── Detalhes expandidos ──
    const renderRowDetails = (row) => (
        <>
            <div className="text-muted font-size-11 fw-bold text-uppercase d-flex align-items-center gap-3 flex-wrap">
                <span><i className="mdi mdi-calendar me-1"></i>Vencimento: {formatDate(row.dueDate)}</span>
                {row.paymentDate && (
                    <>
                        <span>|</span>
                        <span><i className="mdi mdi-calendar-check me-1"></i>Pago em: {formatDate(row.paymentDate)}</span>
                    </>
                )}
                {row.paymentMethod && (
                    <>
                        <span>|</span>
                        <span><i className="mdi mdi-cash me-1"></i>Forma: {row.paymentMethod}</span>
                    </>
                )}
            </div>
            <div className="mt-2 d-flex justify-content-between align-items-center border-top pt-2">
                <div>
                    <span className="fw-semibold font-size-13 text-dark neo-font-outfit d-block">
                        {row.description?.toUpperCase()}
                    </span>
                    {row.entityName && (
                        <small className="text-muted font-size-11">Fornecedor: {row.entityName}</small>
                    )}
                </div>
                <span className="fw-bold font-size-14 text-danger neo-font-outfit">
                    {formatCurrency(row.grossAmount)}
                </span>
            </div>
        </>

    );

    // ── Summary para o NeoFinancialTable ──
    const payableSummary = summary ? [
        {
            label: 'Pendente',
            value: formatCurrency(summary.totalPending),
            count: summary.countPending,
            icon: 'mdi-clock-outline',
            statusClass: 'status-pending'
        },
        {
            label: 'Atrasado',
            value: formatCurrency(summary.totalOverdue),
            count: summary.countOverdue,
            icon: 'mdi-alert-circle-outline',
            statusClass: 'status-overdue'
        },
        {
            label: 'Pago',
            value: formatCurrency(summary.totalPaid),
            icon: 'mdi-check-circle-outline',
            statusClass: 'status-paid'
        }
    ] : [];

    // ── Componente de filtro de status (header actions) ──
    const statusFilterComponent = (
        <div className="neo-filter-select-sm">
            <NeoInput
                type="select"
                value={statusFilter}
                onChange={(e) => onStatusChange(e.target.value)}
                size="sm"
                className="mb-0"
            >
                {PAYABLE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </NeoInput>
        </div>
    );

    return (
        <>
            <NeoFinancialTable
                columns={payableColumns}
                data={records}
                keyField="id"
                loading={loading}
                summaryData={payableSummary}
                summaryComponent={statusFilterComponent}
                renderRowDetails={renderRowDetails}
                searchValue={search}
                onSearchChange={onSearchChange}
                searchPlaceholder="Buscar por descrição, fornecedor..."
                defaultItemsPerPage={20}
                itemsPerPageOptions={[10, 20, 50]}
                emptyMessage="Nenhuma despesa encontrada"
                emptySubMessage="Ajuste os filtros ou use o botão 'Nova Despesa' para registrar."
            />

            {/* Load More (Firebase pagination) */}
            {hasMore && (
                <div className="text-center py-3 border-top">
                    <NeoButton
                        variant="outline-secondary"
                        size="sm"
                        onClick={onLoadMore}
                        loading={loading}
                        loadingText="Carregando..."
                        className="px-4"
                    >
                        Carregar mais
                    </NeoButton>
                </div>
            )}

            {/* Modal de baixa profissional */}
            <PaymentConfirmModal
                isOpen={showPaymentModal}
                toggle={() => { setShowPaymentModal(false); setPaymentRecord(null); }}
                record={paymentRecord}
                type={FINANCIAL_TYPES.LIABILITY}
                onConfirm={handleConfirmPayment}
                processing={processing}
            />
        </>
    );
};

export default PayableTable;
